/**
 *	The portal's own Markdoc tags, laid over the renderer's baseline — see
 *	@shared/docs/markdoc for what every documentation page gets regardless.
 *
 *	Both exports are module constants rather than literals built at the call
 *	site: <DocumentsProvider> memoizes on their identity, and a fresh object per
 *	render would rebuild the config and drop every cached transform.
 */
import type {
	Config,
	Node,
	RenderableTreeNode,
	RenderableTreeNodes,
	Schema,
} from '@markdoc/markdoc';
import { Tag, nodes } from '@markdoc/markdoc';

import { baseConfig } from '@shared/docs/markdoc';
import type { DocumentComponents } from '@shared/docs/provider';

import { Examples } from '@portal/views/_shared/examples';
import { ReferenceHeading } from '@portal/views/_shared/reference-heading';
import { StepNumber } from '@portal/views/_shared/step-number';

const EXAMPLES_TAG = 'Examples';
const STEP_NUMBER_TAG = 'StepNumber';
const REFERENCE_HEADING_TAG = 'ReferenceHeading';

const STEP_PREFIX = /^\((\d+)\)(?=\s)/;

/** The page's directory, collection included: `docs/keys/README.md` is `docs/keys`. */
function toPageKey(config: Config): string {
	return String(config.variables?.['page'] ?? '')
		.split('/')
		.slice(0, -1)
		.join('/');
}

/** `{% examples name="rotate" /%}` renders `_examples/rotate.*` beside the page. */
const examples: Schema = {
	render: EXAMPLES_TAG,
	attributes: {
		name: { type: String, required: true },
	},
	transform(node: Node, config: Config): RenderableTreeNode {
		const { name } = node.transformAttributes(config);

		return new Tag(EXAMPLES_TAG, { page: toPageKey(config), name }, []);
	},
};

/**
 *	`## (1) Title` draws the number as a step. A reference page's title becomes
 *	its heading block, with its frontmatter `group`, `method` and `endpoint`.
 */
const heading: Schema = {
	...nodes.heading,
	transform(node: Node, config: Config): RenderableTreeNodes {
		const level = Number(node.attributes['level']);

		const prefix: RenderableTreeNode[] = [];
		let children = node.transformChildren(config);

		const [first] = children;
		const step = typeof first === 'string' ? STEP_PREFIX.exec(first) : null;

		if (level > 1 && typeof first === 'string' && step) {
			prefix.push(new Tag(STEP_NUMBER_TAG, { value: Number(step[1]) }, []));
			children = [
				first.slice(step[0].length).trimStart(),
				...children.slice(1),
			];
		}

		const frontmatter = config.variables?.['frontmatter'] as Optional<
			Record<string, string>
		>;

		const method = frontmatter?.['method'];
		const endpoint = frontmatter?.['endpoint'];

		if (level === 1 && method && endpoint) {
			return new Tag(
				REFERENCE_HEADING_TAG,
				{ method, endpoint, group: frontmatter?.['group'] },
				children
			);
		}

		return new Tag(`h${level}`, node.transformAttributes(config), [
			...prefix,
			...children,
		]);
	},
};

const RFC_MENTION = /\bRFC (\d+)\b/g;

const IN_LINK = 'inLink';

/** Marks its children as already linked, so `text` below leaves them alone. */
const link: Schema = {
	...baseConfig.nodes?.['link'],
	transform(node: Node, config: Config): RenderableTreeNodes {
		const linked = {
			...config,
			variables: { ...config.variables, [IN_LINK]: true },
		};

		// The shared link transform is synchronous, see @shared/docs/markdoc.
		return (baseConfig.nodes?.['link']?.transform?.(node, linked) ??
			null) as RenderableTreeNodes;
	},
};

/** `RFC 7523` in prose links to the RFC Editor's page for it. */
const text: Schema = {
	...nodes.text,
	transform(node: Node, config: Config): RenderableTreeNodes {
		const content = String(node.attributes['content'] ?? '');

		if (config.variables?.[IN_LINK]) {
			return content;
		}

		const parts: RenderableTreeNode[] = [];
		let consumed = 0;

		for (const match of content.matchAll(RFC_MENTION)) {
			parts.push(content.slice(consumed, match.index));
			parts.push(
				new Tag(
					'a',
					{
						href: `https://www.rfc-editor.org/info/rfc${match[1]}`,
						target: '_blank',
						rel: 'noreferrer noopener',
					},
					[match[0]]
				)
			);
			consumed = match.index + match[0].length;
		}

		if (consumed === 0) {
			return content;
		}

		parts.push(content.slice(consumed));

		return parts.filter((part) => part !== '');
	},
};

export const config: Config = {
	tags: { examples },
	nodes: { heading, link, text },
	variables: {},
};

export const components: DocumentComponents = {
	[EXAMPLES_TAG]: Examples,
	[STEP_NUMBER_TAG]: StepNumber,
	[REFERENCE_HEADING_TAG]: ReferenceHeading,
};
